/** @format */

import { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  orderBy,
  limit,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "../contexts/AuthContext";

export interface BookReview {
  id?: string;
  bookId: string;
  userId: string;
  rating: number;
  review: string;
  createdAt: Date;
  userDisplayName?: string;
}

export interface BookRatingStats {
  averageRating: number;
  totalReviews: number;
  ratingDistribution: {
    1: number;
    2: number;
    3: number;
    4: number;
    5: number;
  };
}

export const useBookReviews = (bookId?: string) => {
  const { currentUser } = useAuth();
  const [reviews, setReviews] = useState<BookReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userReview, setUserReview] = useState<BookReview | null>(null);
  const [ratingStats, setRatingStats] = useState<BookRatingStats | null>(null);
  const [userOwnsBook, setUserOwnsBook] = useState(false);

  // Check if current user owns this book
  const checkUserOwnsBook = async (userId: string, bookId: string) => {
    try {
      // Check in Mybooks collection
      const mybooksQuery = query(
        collection(db, "Mybooks"),
        where("userId", "==", userId),
        where("bookId", "==", bookId)
      );

      const mybooksSnapshot = await getDocs(mybooksQuery);
      return !mybooksSnapshot.empty;
    } catch (err) {
      console.error("Error checking book ownership:", err);
      return false;
    }
  };

  // Fetch user display name from students collection
  const fetchUserDisplayName = async (userId: string): Promise<string> => {
    try {
      // First try to get from students collection
      const studentDoc = await getDoc(doc(db, "students", userId));
      if (studentDoc.exists()) {
        const userData = studentDoc.data();
        return userData.name || "Anonymous";
      }

      // Fallback: check users collection (admin panel users)
      const adminUserDoc = await getDoc(doc(db, "users", userId));
      if (adminUserDoc.exists()) {
        const userData = adminUserDoc.data();
        return userData.name || "Anonymous";
      }

      return "Anonymous";
    } catch (error) {
      console.error("Error fetching user display name:", error);
      return "Anonymous";
    }
  };

  // Fetch reviews for a specific book
  const fetchBookReviews = async (bookId: string) => {
    try {
      setLoading(true);
      setError(null);

      // Check if current user owns this book
      if (currentUser) {
        const ownsBook = await checkUserOwnsBook(currentUser.uid, bookId);
        setUserOwnsBook(ownsBook);
      }

      const q = query(
        collection(db, "BooksReviews"),
        where("bookId", "==", bookId),
        orderBy("createdAt", "desc")
      );

      const querySnapshot = await getDocs(q);
      let reviewsData: BookReview[] = [];

      // Process real reviews from database
      for (const docSnapshot of querySnapshot.docs) {
        const reviewData = docSnapshot.data();

        // Fetch user display name for each review
        const displayName = await fetchUserDisplayName(reviewData.userId);

        reviewsData.push({
          id: docSnapshot.id,
          bookId: reviewData.bookId,
          userId: reviewData.userId,
          rating: reviewData.rating || 0,
          review: reviewData.review || "",
          createdAt: reviewData.createdAt?.toDate() || new Date(),
          userDisplayName: displayName,
        });
      }

      // If no real reviews exist, don't add mock data
      setReviews(reviewsData);

      // Calculate rating statistics
      calculateRatingStats(reviewsData);

      // Check if current user has already reviewed this book
      if (currentUser) {
        const existingReview = reviewsData.find(
          (review) => review.userId === currentUser.uid
        );
        setUserReview(existingReview || null);
      }
    } catch (err) {
      console.error("Error fetching book reviews:", err);
      setError("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  // Add a new review (only if user owns the book)
  const addReview = async (bookId: string, rating: number, review: string) => {
    if (!currentUser) {
      throw new Error("You must be logged in to add a review");
    }

    if (!userOwnsBook) {
      throw new Error("You can only review books that you own");
    }

    try {
      setLoading(true);
      setError(null);

      const newReview: Omit<BookReview, "id"> = {
        bookId,
        userId: currentUser.uid,
        rating,
        review,
        createdAt: new Date(),
        userDisplayName: currentUser.name || "Anonymous",
      };

      const docRef = await addDoc(collection(db, "BooksReviews"), newReview);

      const reviewWithId: BookReview = {
        ...newReview,
        id: docRef.id,
      };

      // Update local state
      setReviews((prev) => [reviewWithId, ...prev]);
      setUserReview(reviewWithId);

      // Recalculate rating statistics
      calculateRatingStats([reviewWithId, ...reviews]);

      return reviewWithId;
    } catch (err) {
      console.error("Error adding review:", err);
      setError("Failed to add review");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Update an existing review
  const updateReview = async (
    reviewId: string,
    rating: number,
    review: string
  ) => {
    try {
      setLoading(true);
      setError(null);

      await updateDoc(doc(db, "BooksReviews", reviewId), {
        rating,
        review,
        createdAt: new Date(),
      });

      // Update local state
      const updatedReview: BookReview = {
        ...userReview!,
        rating,
        review,
        createdAt: new Date(),
      };

      setReviews((prev) =>
        prev.map((r) => (r.id === reviewId ? updatedReview : r))
      );
      setUserReview(updatedReview);

      // Recalculate rating statistics
      calculateRatingStats(
        reviews.map((r) => (r.id === reviewId ? updatedReview : r))
      );

      return updatedReview;
    } catch (err) {
      console.error("Error updating review:", err);
      setError("Failed to update review");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Delete a review
  const deleteReview = async (reviewId: string) => {
    try {
      setLoading(true);
      setError(null);

      await deleteDoc(doc(db, "BooksReviews", reviewId));

      // Update local state
      setReviews((prev) => prev.filter((r) => r.id !== reviewId));
      setUserReview(null);

      // Recalculate rating statistics
      calculateRatingStats(reviews.filter((r) => r.id !== reviewId));
    } catch (err) {
      console.error("Error deleting review:", err);
      setError("Failed to delete review");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Calculate rating statistics
  const calculateRatingStats = (reviewsList: BookReview[]) => {
    if (reviewsList.length === 0) {
      setRatingStats({
        averageRating: 0,
        totalReviews: 0,
        ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
      });
      return;
    }

    const totalRating = reviewsList.reduce(
      (sum, review) => sum + review.rating,
      0
    );
    const averageRating = totalRating / reviewsList.length;

    const ratingDistribution = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    reviewsList.forEach((review) => {
      if (review.rating >= 1 && review.rating <= 5) {
        ratingDistribution[review.rating as keyof typeof ratingDistribution]++;
      }
    });

    setRatingStats({
      averageRating: Math.round(averageRating * 10) / 10, // Round to 1 decimal place
      totalReviews: reviewsList.length,
      ratingDistribution,
    });
  };

  // Check if user can review (owns the book and hasn't already reviewed it)
  const canUserReview = () => {
    return currentUser && userOwnsBook && !userReview;
  };

  // Get user's existing review
  const getUserReview = () => {
    return userReview;
  };

  // Load reviews when bookId changes
  useEffect(() => {
    if (bookId) {
      fetchBookReviews(bookId);
    }
  }, [bookId, currentUser]);

  return {
    reviews,
    loading,
    error,
    userReview,
    ratingStats,
    userOwnsBook,
    addReview,
    updateReview,
    deleteReview,
    canUserReview,
    getUserReview,
    fetchBookReviews,
  };
};
